import { ProgressCircle, Table } from '@heroui/react';
import { Sheet } from '@heroui-pro/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { trpc } from '@/lib/trpc';
import { SheetOverlayPortalHost } from '@/components/SheetOverlayPortalProvider';

export interface SpaceEntitlementsSheetProps {
  open: boolean;
  spaceId: string;
  spaceName: string;
  onCancel: () => void;
}

type SpaceEntitlementRow = {
  id: string;
  resourceCode?: string | null;
  quota?: number | string | null;
  used?: number | string | null;
  remaining?: number | string | null;
  resource?: {
    name?: string | null;
    unit?: string | null;
  } | null;
};

function toNumber(value: number | string | null | undefined): number {
  if (value == null) return 0;
  const parsed = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatAmount(value: number): string {
  return value.toLocaleString('zh-CN');
}

function getUsageProgress(quota: number, used: number): number {
  if (quota <= 0) return used > 0 ? 100 : 0;
  return Math.min(100, Math.round((used / quota) * 100));
}

function getProgressColor(progress: number): 'danger' | 'warning' | 'accent' {
  if (progress >= 100) return 'danger';
  if (progress >= 80) return 'warning';
  return 'accent';
}

export function SpaceEntitlementsSheet({
  open,
  spaceId,
  spaceName,
  onCancel,
}: SpaceEntitlementsSheetProps) {
  const [listReady, setListReady] = useState(false);
  const [overlayPortalHost, setOverlayPortalHost] = useState<HTMLElement | null>(null);

  const { data, isLoading, refetch } = trpc.spaceEntitlements.listAll.useQuery(
    { spaceId },
    { enabled: open }
  );

  useEffect(() => {
    if (!open) {
      setListReady(false);
      return;
    }
    let cancelled = false;
    refetch().then(() => {
      if (!cancelled) setListReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [open, spaceId, refetch]);

  const rows = useMemo(() => (data ?? []) as SpaceEntitlementRow[], [data]);

  const handleSheetOpenChange = useCallback(
    (next: boolean) => {
      if (!next) onCancel();
    },
    [onCancel]
  );

  return (
    <Sheet isDismissable isOpen={open} placement="right" onOpenChange={handleSheetOpenChange}>
      <Sheet.Backdrop>
        <Sheet.Content className="w-[min(720px,90vw)]">
          <SheetOverlayPortalHost onHostChange={setOverlayPortalHost}>
            <Sheet.Dialog className="flex h-[100dvh] flex-col">
              <Sheet.Header className="shrink-0 gap-1">
                <Sheet.Heading className="text-xl font-medium">
                  空间权益管理 - {spaceName}
                </Sheet.Heading>
              </Sheet.Header>
              <Sheet.Body className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4">
                {!listReady ? (
                  <div className="text-muted-foreground flex flex-1 items-center justify-center text-sm">
                    加载中…
                  </div>
                ) : (
                  <>
                    <Table>
                      <Table.ScrollContainer>
                        <Table.Content aria-label="空间权益列表">
                          <Table.Header>
                            <Table.Column id="resource" isRowHeader className="min-w-[140px]">
                              资源
                            </Table.Column>
                            <Table.Column id="quota" className="min-w-[7rem]">
                              总额
                            </Table.Column>
                            <Table.Column id="used" className="min-w-[5rem]">
                              已用
                            </Table.Column>
                            <Table.Column id="remaining" className="min-w-[7rem]">
                              剩余
                            </Table.Column>
                          </Table.Header>
                          <Table.Body>
                            {isLoading ? (
                              <Table.Row id="__loading__" key="__loading__">
                                <Table.Cell
                                  className="text-muted-foreground text-center"
                                  colSpan={5}
                                >
                                  加载中…
                                </Table.Cell>
                              </Table.Row>
                            ) : rows.length === 0 ? (
                              <Table.Row id="__empty__" key="__empty__">
                                <Table.Cell
                                  className="text-muted-foreground text-center"
                                  colSpan={5}
                                >
                                  当前空间暂无权益记录
                                </Table.Cell>
                              </Table.Row>
                            ) : (
                              rows.map((row, idx) => {
                                const quota = toNumber(row.quota);
                                const used = toNumber(row.used);
                                const remaining = toNumber(row.remaining);
                                const progress = getUsageProgress(quota, used);
                                const resourceName =
                                  row.resource?.name ?? row.resourceCode ?? '未知资源';

                                return (
                                  <Table.Row
                                    key={row.id}
                                    id={row.id}
                                    className={
                                      idx === rows.length - 1 ? '[&_td]:border-b-0' : ''
                                    }
                                  >
                                    <Table.Cell>
                                      <div className="flex min-w-0 items-center gap-3">
                                        <ProgressCircle
                                          aria-label={`${resourceName} 使用进度`}
                                          className="shrink-0"
                                          color={getProgressColor(progress)}
                                          size="sm"
                                          value={progress}
                                        >
                                          <ProgressCircle.Track>
                                            <ProgressCircle.TrackCircle />
                                            <ProgressCircle.FillCircle />
                                          </ProgressCircle.Track>
                                        </ProgressCircle>
                                        <span className="text-foreground truncate text-sm font-medium">
                                          {resourceName}
                                        </span>
                                      </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                      <span className="text-foreground text-sm font-semibold tabular-nums">
                                        {formatAmount(quota)}
                                      </span>
                                    </Table.Cell>
                                    <Table.Cell>
                                      <span className="text-foreground text-sm font-semibold tabular-nums">
                                        {formatAmount(used)}
                                      </span>
                                    </Table.Cell>
                                    <Table.Cell>
                                      <span className="text-foreground text-sm font-semibold tabular-nums">
                                        {formatAmount(remaining)}
                                      </span>
                                    </Table.Cell>
                                  </Table.Row>
                                );
                              })
                            )}
                          </Table.Body>
                        </Table.Content>
                      </Table.ScrollContainer>
                    </Table>
                  </>
                )}
              </Sheet.Body>
            </Sheet.Dialog>

          </SheetOverlayPortalHost>
        </Sheet.Content>
      </Sheet.Backdrop>
    </Sheet>
  );
}
